import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LuTrophy, LuSun, LuUsers, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import "../../styles/events/events.css"
import EventCard from "./eventCard";
import useEvents from "../../utils/useEvents";
import { EVENT_CATEGORY_SELECTED } from "../../reducers/eventCategoryReducer";
import { getCategoryLabel, getCurrentEvents, getPastEvents, getEventStatus } from "../../utils/events";
import { getNumericDate, getShortWrittenMonth, getNumericYear, getStoredNow } from "../../utils/date"



//REGROUPEMENT DES CATÉGORIES POUR LES FILTRES (une catégorie absente de la liste va dans "Vie du club")
const CATEGORY_GROUPS = [
  { id: "tournois", label: "Tournois", icon: LuTrophy, tone: "red", categories: ["Tournoi du carnaval", "Tournoi des Fêtes", "Tournoi de la rentrée", "Tournoi Basko Beach", "Finale BLVB"] },
  { id: "beach", label: "Beach-volley", icon: LuSun, tone: "sand", categories: ["Animation estivale de Beach-Volley", "Beach-Volley VBLC"] },
  { id: "club", label: "Vie du club", icon: LuUsers, tone: "green", categories: [] },
];

const getGroup = (category) => CATEGORY_GROUPS.find((group) => group.categories.includes(category)) || CATEGORY_GROUPS[CATEGORY_GROUPS.length - 1];

const formatShortDate = (date) => `${getNumericDate(date)} ${getShortWrittenMonth(date).toLowerCase()} ${getNumericYear(date)}`;


function Events() {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  //TOUS LES ÉVÈNEMENTS (mémoire du navigateur puis API)
  const events = useEvents();
  const [selectedGroupId, setSelectedGroupId] = useState("all");

  //ÉVÈNEMENTS EN COURS D'AFFICHAGE, du plus proche au plus lointain
  const now = getStoredNow();
  const incomingEvents = getCurrentEvents(events, now);

  //DÉFILEMENT DES CARTES : les flèches n'apparaissent que si les cartes débordent de l'encadré
  const upcomingTrackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const track = upcomingTrackRef.current;
    if (!track) return;
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener("resize", updateScrollButtons);
    return () => window.removeEventListener("resize", updateScrollButtons);
  }, [incomingEvents.length, updateScrollButtons]);

  const scrollUpcoming = (direction) => {
    const track = upcomingTrackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
  };

  const goToEventsDetailsFromThumbnail = (e) => {
    dispatch({ type: EVENT_CATEGORY_SELECTED, payload: getCategoryLabel(e) })
    navigate('/eventsDetails')
  };

  //Fonction qui met dans un reducer le nom de la catégorie et qui fait naviguer vers la page eventsDetails
  const goToEventsDetailsFromList = (category) => {
    dispatch({ type: EVENT_CATEGORY_SELECTED, payload: category })
    navigate('/eventsDetails')
  };

  //UNE TUILE PAR CATÉGORIE : nombre d'éditions + prochaine date (ou dernière date passée)
  //Les évènements pas encore publiés (avant leur début d'affichage) n'apparaissent pas
  const publishedEvents = events.filter((e) => getEventStatus(e, now) !== "scheduled");
  const categories = [...new Set(publishedEvents.map(getCategoryLabel))].map((category) => {
    const categoryEvents = publishedEvents.filter((e) => getCategoryLabel(e) === category);
    const next = getCurrentEvents(categoryEvents, now)[0];
    const last = getPastEvents(categoryEvents, now)[0];
    return { category, count: categoryEvents.length, next, last, group: getGroup(category) };
  }).sort((a, b) => a.category.localeCompare(b.category, "fr"));

  const filters = [
    { id: "all", label: "Tous", count: categories.length },
    ...CATEGORY_GROUPS.map((group) => ({ id: group.id, label: group.label, count: categories.filter((c) => c.group.id === group.id).length })),
  ].filter((f) => f.id === "all" || f.count > 0);

  const visibleCategories = selectedGroupId === "all" ? categories : categories.filter((c) => c.group.id === selectedGroupId);


  return (
    <main className="events">

      {/* EN-TÊTE */}
      <header className="page-hero">
        <div className="wrap page-hero_content">
          <p className="page-hero_eyebrow">
            <span className="tricolor" aria-hidden="true"><span></span><span></span><span></span></span>
            Actualités
          </p>
          <h1 className="page-hero_title">Toutes les actualités du VBLC</h1>
          <p className="page-hero_text">Tournois en salle et sur le sable, temps forts de la saison, informations pour les adhérents : retrouvez ici tout ce qui fait vivre le club.</p>
        </div>
      </header>

      {/* ÉVÈNEMENTS EN COURS ET À VENIR : centrés s'ils tiennent dans l'encadré, sinon défilables */}
      <div className="wrap upcoming_wrap">
        <section className="upcoming surface" aria-labelledby="upcoming-title">
          <div className="upcoming_header">
            <h2 id="upcoming-title" className="title-xl">À la une</h2>
            {(canScrollPrev || canScrollNext) && (
              <div className="upcoming_nav">
                <button className="btn btn--secondary btn--icon" onClick={() => scrollUpcoming(-1)} disabled={!canScrollPrev} aria-label="Actualités précédentes">
                  <LuChevronLeft aria-hidden="true" />
                </button>
                <button className="btn btn--secondary btn--icon" onClick={() => scrollUpcoming(1)} disabled={!canScrollNext} aria-label="Actualités suivantes">
                  <LuChevronRight aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          {incomingEvents.length > 0
            ? <div className="upcoming_track" ref={upcomingTrackRef} onScroll={updateScrollButtons}>
              {incomingEvents.map((e, index) => (
                <EventCard key={index} event={e} onSelect={goToEventsDetailsFromThumbnail} />
              ))}
            </div>
            : <div className="events_empty">
              <p>Aucune actualité pour le moment. Suivez-nous sur les réseaux sociaux pour ne rien manquer !</p>
            </div>}

        </section>
      </div>

      {/* TOUS LES ÉVÈNEMENTS, FILTRABLES */}
      <section className="section section--white" aria-labelledby="all-title">
        <div className="wrap">
          <div className="section_header">
            <div className="section_heading">
              <p className="eyebrow">Archives</p>
              <h2 id="all-title" className="title-xl">Tous les évènements et communications</h2>
              <p className="section_intro">Retrouvez chaque évènement et communication du club, avec toutes leurs éditions.</p>
            </div>
          </div>

          {categories.length > 0 && (
            <div className="events_filters" role="group" aria-label="Filtrer par type d’évènement">
              {filters.map((f) => (
                <button
                  key={f.id}
                  className="events_filter"
                  aria-pressed={selectedGroupId === f.id}
                  onClick={() => setSelectedGroupId(f.id)}
                >
                  {f.label} <span className="events_filter_count">{f.count}</span>
                </button>
              ))}
            </div>
          )}

          <ul className="categories_grid">
            {visibleCategories.map(({ category, count, next, last, group }) => {
              const Icon = group.icon;
              return (
                <li key={category}>
                  <button className="category_tile" onClick={() => goToEventsDetailsFromList(category)}>
                    <span className={`icon-tile icon-tile--${group.tone}`} aria-hidden="true"><Icon /></span>
                    <span className="category_tile_body">
                      <span className="category_tile_group">{group.label}</span>
                      <span className="category_tile_name">{category}</span>
                      <span className="category_tile_meta">
                        {count} {count > 1 ? "éditions" : "édition"}
                        {next
                          ? <> · <strong className="category_tile_next">{next.startDate ? `Prochaine : ${formatShortDate(next.startDate)}` : "À venir"}</strong></>
                          : last?.startDate && <> · Dernière : {formatShortDate(last.startDate)}</>}
                      </span>
                    </span>
                    <LuChevronRight className="category_tile_chevron" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </main>
  )
};

export default Events;
